// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Stack } from 'expo-router';
import { ScrollView, StyleSheet } from 'react-native';

import { Text, View } from '@/components/Themed';
import { useI18n } from '@/i18n';

/**
 * The service conditions, served at goffy.app/terms.
 *
 * Sibling of the privacy policy: same shape, same reasons. It lives in the app so
 * it ships and versions with the code it describes, and it is the document the
 * register screen's consent checkbox points at — a checkbox nobody can read the
 * terms behind is not consent. Change LAST_UPDATED whenever the substance changes.
 */
const LAST_UPDATED = '2026-08-07';

const SECTIONS = [
  ['terms.accountH', 'terms.accountP'],
  ['terms.fairUseH', 'terms.fairUseP'],
  ['terms.dataH', 'terms.dataP'],
  ['terms.warrantyH', 'terms.warrantyP'],
  ['terms.changesH', 'terms.changesP'],
] as const;

export default function TermsScreen() {
  const { t } = useI18n();

  return (
    <>
      <Stack.Screen options={{ title: t('terms.title') }} />
      <ScrollView contentContainerStyle={styles.container}>
        <Text style={styles.title}>{t('terms.title')}</Text>
        <Text style={styles.updated}>{t('terms.updated', { date: LAST_UPDATED })}</Text>
        <Text style={styles.intro}>{t('terms.intro')}</Text>

        {SECTIONS.map(([heading, body]) => (
          <View key={heading} style={styles.section}>
            <Text style={styles.heading}>{t(heading)}</Text>
            <Text style={styles.paragraph}>{t(body)}</Text>
          </View>
        ))}
      </ScrollView>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 20,
    paddingBottom: 48,
    maxWidth: 720,
    alignSelf: 'center',
    width: '100%',
  },
  title: {
    fontSize: 26,
    fontWeight: '700',
  },
  updated: {
    fontSize: 14,
    opacity: 0.7,
    marginTop: 2,
    marginBottom: 16,
  },
  intro: {
    fontSize: 16,
    lineHeight: 24,
    marginBottom: 20,
  },
  section: {
    marginBottom: 18,
  },
  heading: {
    fontSize: 17,
    fontWeight: '600',
    marginBottom: 6,
  },
  paragraph: {
    fontSize: 16,
    lineHeight: 24,
  },
});
